import type { FC } from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Cached, Cancel, Lock as LockIcon, PostAdd } from '@mui/icons-material';
import { useEnqueueSnackbar } from 'hooks/useEnqueueSnackbar';
import {
  OverviewEntryContainer,
  ButtonStyled,
} from 'features/cabinet/dashboard';
import { queryKeys, useUniqueId } from '../../facebook/api';
import copy from 'copy-to-clipboard';
import { queryClient } from 'lib/react-query';
import { ErrorBoundary } from 'react-error-boundary';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import Stack from '@mui/material/Stack';
import { usePermissionGuard } from 'features/login';

const ErrorBoundaryFallback = ({
  resetErrorBoundary,
}: {
  resetErrorBoundary: () => void;
}) => {
  return (
    <Stack
      sx={{
        flexDirection: 'column',
        alignItems: 'start',
        justifyContent: 'center',
        gap: 2,
        p: 2,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'start',
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(211, 47, 47, 0.04)',
          }}
        >
          <Cancel color={'error'} sx={{ fontSize: 28 }} />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'start',
            justifyContent: 'start',
          }}
        >
          <Typography
            variant={'body2'}
            sx={(t) => ({ color: t.palette.error.main })}
            fontWeight={500}
          >
            Failed to create new ID
          </Typography>
          <IconButton
            size="small"
            color="inherit"
            onClick={resetErrorBoundary}
            sx={{
              p: 0.5,
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'start',
              justifyContent: 'start',
              gap: 0.5,
              borderRadius: 0,
              ml: '-3px',
            }}
            disableRipple
            disableFocusRipple
            disableTouchRipple
          >
            <Cached fontSize="small" />
            <Typography
              variant={'caption'}
              sx={(t) => ({
                textAlign: 'left',
                color: t.palette.text.primary,
                textTransform: 'uppercase',
                fontWeight: 500,
              })}
            >
              Try again
            </Typography>
          </IconButton>
        </Box>
      </Box>
    </Stack>
  );
};

const GetNewUniqueIdComponent: FC = memo(() => {
  const STATES = useRef({
    IDLE: 'idle',
    LOADING: 'loading',
    COPIED: 'copied',
    ERROR: 'error',
  });
  const { accessToGenerateId } = usePermissionGuard();
  const { onShowAlert } = useEnqueueSnackbar();
  const [currentState, setCurrentState] = useState(STATES.current.IDLE);
  const isStateLoading = currentState === STATES.current.LOADING;

  const {
    data,
    refetch: getCampaignId,
    isRefetching,
    isLoading,
    isError,
    error,
  } = useUniqueId({
    config: {
      enabled: false,
    },
  });

  const handleSetIdle = useCallback(() => {
    setTimeout(() => {
      setCurrentState(STATES.current.IDLE);
      queryClient.removeQueries({
        queryKey: [queryKeys.uniqueId],
      });
    }, 3000);
  }, [setCurrentState, STATES]);

  const handleClick = useCallback(() => {
    if (currentState !== STATES.current.IDLE) return;

    setCurrentState(STATES.current.LOADING);
    void getCampaignId();
  }, [currentState, STATES, getCampaignId, setCurrentState]);

  useEffect(() => {
    if (isRefetching || isLoading) {
      setCurrentState(STATES.current.LOADING);
    }
  }, [isRefetching, isLoading]);

  useEffect(() => {
    if (isError) {
      setCurrentState(STATES.current.ERROR);
      throw Error(error as never);
    }
  }, [isError, error]);

  useEffect(() => {
    if (data?.unique_id && currentState === STATES.current.LOADING) {
      copy(data.unique_id);
      onShowAlert(`Campaign ID: ${data.unique_id} copied!`);
      setCurrentState(STATES.current.COPIED);

      handleSetIdle();
    }
  }, [data, currentState, handleSetIdle, setCurrentState, onShowAlert]);

  const initialButtonProps: () => { title: string; disabled: boolean } =
    useCallback(() => {
      switch (currentState) {
        case STATES.current.LOADING:
          return {
            title: 'Generating ID..',
            disabled: true,
          };
        case STATES.current.COPIED:
          return {
            title: `ID copied: ${data?.unique_id}`,
            disabled: false,
          };
        case STATES.current.IDLE:
        default:
          return {
            title: 'Get new ID',
            disabled: false,
          };
      }
    }, [data, STATES, currentState]);

  return accessToGenerateId ? (
    <ButtonStyled
      variant={'text'}
      size={'small'}
      onClick={handleClick}
      disabled={isStateLoading || initialButtonProps().disabled}
    >
      <Stack
        sx={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'start',
          gap: isStateLoading ? 1.5 : 2,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: isStateLoading
              ? 'transparent'
              : 'rgba(41, 121, 255, 0.04)',
          }}
        >
          {isStateLoading ? (
            <CircularProgress size={28} color="primary" />
          ) : (
            <PostAdd color={'primary'} sx={{ fontSize: 28 }} />
          )}
        </Box>
        <Typography
          variant={'body2'}
          textTransform={isStateLoading ? 'initial' : 'uppercase'}
          sx={{ textAlign: 'left' }}
          fontWeight={isStateLoading ? 400 : 500}
        >
          {initialButtonProps().title}
          {currentState === STATES.current.IDLE ? (
            <Typography
              component={'span'}
              variant={'body2'}
              color={'gray'}
              sx={{ textTransform: 'none', pl: 0.5 }}
            >
              (All networks)
            </Typography>
          ) : null}
        </Typography>
      </Stack>
    </ButtonStyled>
  ) : (
    <OverviewEntryContainer>
      <ButtonStyled
        variant={'text'}
        size={'small'}
        onClick={() => null}
        disabled
      >
        <Stack
          sx={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'start',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LockIcon
              sx={(t) => ({ fontSize: 32, color: t.palette.action.disabled })}
            />
          </Box>
          <Typography
            variant={'body2'}
            sx={{ textAlign: 'left' }}
            fontWeight={400}
          >
            No access to generate ID
          </Typography>
        </Stack>
      </ButtonStyled>
    </OverviewEntryContainer>
  );
});

GetNewUniqueIdComponent.displayName = 'GetNewUniqueIdComponent';

const GetNewUniqueId = () => {
  return (
    <ErrorBoundary
      FallbackComponent={ErrorBoundaryFallback}
      onReset={() => {
        queryClient.removeQueries({
          queryKey: [queryKeys.uniqueId],
        });
      }}
    >
      <GetNewUniqueIdComponent />
    </ErrorBoundary>
  );
};

export default GetNewUniqueId;
